import type { Conversation } from "@casey/db-types";

// Status mapping from DB to UI labels
export const STATUS_LABELS = {
  pending: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  abandoned: "Cancelled",
} as const;

// Status badge variants
export const STATUS_VARIANTS = {
  pending: "outline" as const,
  in_progress: "secondary" as const,
  completed: "default" as const,
  abandoned: "destructive" as const,
} as const;

// Status colors for badges (matching Linear issue requirements)
export const STATUS_COLORS = {
  pending: "bg-gray-100 text-gray-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  completed: "bg-green-100 text-green-800",
  abandoned: "bg-red-100 text-red-800",
} as const;

export function getStatusLabel(status: string | null | undefined): string {
  if (!status) return STATUS_LABELS.pending;
  return STATUS_LABELS[status as keyof typeof STATUS_LABELS] || status;
}

export function getStatusVariant(status: string | null | undefined) {
  if (!status) return STATUS_VARIANTS.pending;
  return STATUS_VARIANTS[status as keyof typeof STATUS_VARIANTS] || "outline";
}

export function getStatusColor(status: string | null | undefined): string {
  if (!status) return STATUS_COLORS.pending;
  return (
    STATUS_COLORS[status as keyof typeof STATUS_COLORS] || STATUS_COLORS.pending
  );
}

// Extract company name from email domain
export function extractCompanyFromEmail(
  email: string | null | undefined,
): string {
  if (!email) return "Unknown Company";

  const domain = email.split("@")[1];
  if (!domain) return "Unknown Company";

  // Remove common TLDs and format as company name
  const company = domain
    ?.split(".")?.[0]
    ?.replace(/[-_]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return company ?? "Unknown Company";
}

// Get participant display info from ConversationsWithProfile
export function getParticipantInfoFromView(
  session: {
    participantProfileId?: string | null;
    participantProfileEmail?: string | null;
    participantProfileFirstName?: string | null;
    participantProfileLastName?: string | null;
    participantProfileOrganizationName?: string | null;
    participantProfileOrganizationDomain?: string | null;
    participantProfileAvatarImageUrl?: string | null;
  } & {
    interviewInviteByInviteCode?: {
      targetName?: string | null;
      targetEmail?: string | null;
    } | null;
  },
) {
  const invite = session.interviewInviteByInviteCode;

  // Use profile data if available
  if (session.participantProfileId) {
    const fullName = [
      session.participantProfileFirstName,
      session.participantProfileLastName,
    ]
      .filter(Boolean)
      .join(" ");

    return {
      name:
        fullName ||
        session.participantProfileEmail?.split("@")[0] ||
        "Anonymous Participant",
      email: session.participantProfileEmail || invite?.targetEmail || null,
      company:
        session.participantProfileOrganizationName ||
        extractCompanyFromEmail(
          session.participantProfileEmail || invite?.targetEmail,
        ),
      domain: session.participantProfileOrganizationDomain || null,
      avatar: session.participantProfileAvatarImageUrl || null,
    };
  }

  // Fall back to invite data
  return {
    name: invite?.targetName || "Anonymous Participant",
    email: invite?.targetEmail || null,
    company: extractCompanyFromEmail(invite?.targetEmail),
    domain: null,
    avatar: null,
  };
}

// Get participant display info (legacy function for backward compatibility)
export function getParticipantInfo(
  session: Conversation & {
    interviewInviteByInviteCode?: {
      targetName?: string | null;
      targetEmail?: string | null;
    } | null;
  },
  profile?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    organizationName?: string | null;
    organizationDomain?: string | null;
    avatarImageUrl?: string | null;
  } | null,
) {
  const invite = session.interviewInviteByInviteCode;

  // Prefer profile data over invite data when available
  if (profile) {
    const fullName = [profile.firstName, profile.lastName]
      .filter(Boolean)
      .join(" ");

    return {
      name: fullName || profile.email?.split("@")[0] || "Anonymous Participant",
      email: profile.email || invite?.targetEmail || null,
      company:
        profile.organizationName ||
        extractCompanyFromEmail(profile.email || invite?.targetEmail),
      domain: profile.organizationDomain || null,
      avatar: profile.avatarImageUrl || null,
    };
  }

  // Fall back to invite data
  return {
    name: invite?.targetName || "Anonymous Participant",
    email: invite?.targetEmail || null,
    company: extractCompanyFromEmail(invite?.targetEmail),
    domain: null,
    avatar: null,
  };
}

// Format duration from timestamps
export function calculateDuration(
  startedAt: string | null | undefined,
  completedAt: string | null | undefined,
): string {
  if (!startedAt || !completedAt) return "—";

  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.round(diffMs / (1000 * 60));

  if (diffMins < 60) {
    return `${diffMins} min`;
  } else {
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
}

// Format date for display with time
// Shows "6:53pm" for today, "Nov 11 at 6:53pm" for current year, "December 19, 2024 at 10:01am" for past years
export function formatSessionDate(date: string | null | undefined): string {
  if (!date) return "—";

  const sessionDate = new Date(date);
  const now = new Date();

  // Check if it's today
  const isToday =
    sessionDate.getDate() === now.getDate() &&
    sessionDate.getMonth() === now.getMonth() &&
    sessionDate.getFullYear() === now.getFullYear();

  const isCurrentYear = sessionDate.getFullYear() === now.getFullYear();

  // Format time as "6:53pm" or "10:01am"
  const timeStr = sessionDate
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  if (isToday) {
    // Just show time for today: "6:53pm"
    return timeStr;
  } else if (isCurrentYear) {
    // "Nov 11 at 6:53pm"
    const dateStr = sessionDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${dateStr} at ${timeStr}`;
  } else {
    // "December 19, 2024 at 10:01am"
    const dateStr = sessionDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
    return `${dateStr} at ${timeStr}`;
  }
}

// Check if user can perform actions on session
export function canArchiveSession(
  session: Conversation,
  userPermissions?: {
    canWriteSessions?: boolean;
  },
): boolean {
  return userPermissions?.canWriteSessions === true && !session.archivedAt;
}

// Check if user can view transcript for ConversationsWithProfile
export function canViewTranscriptFromView(session: {
  status?: string | null;
  archivedAt?: string | null;
}): boolean {
  return (
    (session.status === "completed" || session.status === "in_progress") &&
    !session.archivedAt
  );
}
